/** @jsxImportSource @emotion/react */
import type { CSSProperties } from 'react'
import Moment from 'react-moment'
import moment from 'moment'
import _ from 'lodash'
import {css} from '@emotion/react'
import Button from 'react-bootstrap/Button'

import * as generalHelper from 'helpers/generalHelper'
import { fullName, formalName, nc } from 'helpers/nameHelper'
import routes from '../constants/routes'
import {PAYER_STATUS_OPTIONS} from '../constants/orderStatus'
import Link from 'components/atoms/Link'
import {Pill} from 'components/atoms/Pill'
import Checkbox from 'components/atoms/IndeterminateCheckbox'
import {bucketColumnDefaults, getFollowUpNamespace} from './columnConfig'
import {PopoverWrapper} from 'components/atoms/PopoverWrapper'
import {OrderProgressBar} from 'components/molecules/OrderProgressBar'
import NightOwlIcon from 'assets/img/nightowl-icon.svg'
import {LazyNotesPopover} from './LazyNotesPopover'
import {LazyMessagesPopover} from './LazyMessagesPopover'
import {LazySmsPopover} from './LazySmsPopover'
import {isOrganization} from '../../../helpers/followUpProvidersHelper'

const renderFollowUpDate = (followUp) => {
  if (!followUp?.date) {
    return <span />
  }

  return (
    <span
      css={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        whiteSpace: 'nowrap',
      }}>
      <span>{moment(followUp.date).format('MM/DD/YYYY')}</span>
      {followUp.humanFollowUpRequired && (
        <span
          className="badge badge-warning"
          title={followUp.humanFollowUpReason || 'Human follow-up required'}
          css={{
            fontSize: 10,
            lineHeight: 1,
            padding: '3px 5px',
          }}>
          Human
        </span>
      )}
    </span>
  )
}

const dayClass = (row) => {
  const diff = moment().diff(moment(row.createdAt), 'day')
  if (diff > 21) {
    return 'btn-danger'
  }
  if (diff > 15) {
    return 'btn-orange'
  }
  if (diff > 7) {
    return 'btn-warning'
  }
  return 'btn-secondary'
}

const getDeviceCategory = (row: any) => {
  if (row && row?.device_category) {
    return <span className="font-weight-bold m-2">{row?.device_category?.name}</span>
  }
}

/**
 * Shown only for orders the Outsource access policy makes visible to outsource users
 * (server-computed `outsourceVisible`). Hidden orders are the overwhelming majority,
 * so only the visible state is badged.
 */
const renderOutsourceVisibleBadge = () => (
  <span
    className="badge badge-success ml-1"
    title="Visible to outsource users"
    css={{
      fontSize: 10,
      lineHeight: 1,
      padding: '3px 5px',
    }}>
    Outsource
  </span>
)

/** Shown when the order's active Wesper fulfillment is DROPSHIP (kit ships directly to the patient). */
const renderWesperDropshipBadge = () => (
  <span
    className="badge badge-info ml-1"
    title="Wesper dropship fulfillment"
    css={{
      fontSize: 10,
      lineHeight: 1,
      padding: '3px 5px',
    }}>
    Wesper
  </span>
)

const getPatient = (row: any, type: string, bucket: string) => {
  if (row) {
    if (type === 'name') {
      const label = row.patient
        ? `${fullName(row.patient.firstName, row.patient.lastName)} ${
            row.editSuggested || row.patientInsuranceCorrection ? '⚠️' : ''
          }`.trim()
        : `No patient #${row.id}`
      return (
        <Link
          to={`${routes.index}${routes.order.edit}${row.id}?bucket=${bucket}`}
          label={label}
          style={{ fontSize: '1.1rem' }}
        />
      )
    } else if (type === 'dob' && row.patient && row.patient.dob) {
      return (
        <small>
          <Moment format="MM/DD/YYYY" date={row.patient.dob} />
        </small>
      )
    } else if (type === 'order_id') {
      return (
        <>
          <span className="p-1 border border-primary rounded mt-1 mr-1">
            <Link to={`${routes.index}${routes.order.edit}${row.id}`} label={`#${row.id}`} />
          </span>
          {getDeviceCategory(row)}
        </>
      )
    }
  }
  return ''
}

const renderPayer = (order: any, position: number, style?: CSSProperties) => {
  const payer = order.payers?.find((p) => p.position === position)

  return (
    <PopoverWrapper
      placement="left"
      customHeader="Payor Notes"
      customContent={
        <div className="px-2">
          <p className="px-2 mb-1">
            Member ID: {payer?.memberId}
            <br />
            Authorization #: {order.authorizationNumber}
            <br />
            Diagnosis Codes: {order.diagnosisCodes?.join(', ')}
          </p>
        </div>
      }>
      <span style={style}>{payer?.name ?? payer?.names?.[0] ?? '-'}</span>
    </PopoverWrapper>
  )
}

/**
 * List of table columns
 */
const _columnsFunction = ({
  testType,
  bucket,
  setSelectedIds,
  selectedIds,
  setShowAuthorizationDialog,
  stats,
}: {
  testType: 'POX' | 'COX' | 'HST' | 'PSG'
  bucket: string
  setSelectedIds: (fn: (ids: number[]) => number[]) => void
  selectedIds: number[]
  setShowAuthorizationDialog: (orderId: number) => void
  stats?: { [key: string]: { [key: string]: string } }
}) => {
  const allColumns = [
    {
      id: 'select',
      accessor: 'select',
      disableSortBy: true,
      Header: ({ rows }) => {
        return (
          <Checkbox
            css={{ marginLeft: '5px' }}
            indeterminate={!!(selectedIds?.length && selectedIds?.length < rows.length)}
            checked={selectedIds?.length === rows.length}
            onChange={() =>
              setSelectedIds(
                selectedIds?.length === rows.length
                  ? []
                  : rows.map(({ original }) => Number(original.id)),
              )
            }
          />
        )
      },
      Cell: ({ row }) => (
        <div className="pl-1 pr-2">
          <Checkbox
            checked={selectedIds.includes(Number(row.original.id))}
            onChange={(e) =>
              setSelectedIds((prev) => {
                const orderId = Number(row.original.id)
                if (prev.includes(orderId)) {
                  return prev?.filter((id) => id !== orderId)
                }
                return [...prev, orderId]
              })
            }
          />
        </div>
      ),
    },
    {
      Header: bucket === 'INS_VER' ? 'Order ID' : 'Order Information',
      accessor: 'id', // accessor is the "key" in the data,
      className: 'col-3',
      sortAccessor: ['firstName', 'lastName'],
      filterAccessor: ['id', 'firstName', 'lastName'],
      Cell: (props) => {
        const row = props.row.original
        if (bucket === 'INS_VER') {
          return (
            <div
              css={{
                '& .order-info:hover': {
                  '& .open-new-tab': {
                    display: 'block',
                  },
                },
              }}>
              <div className="d-flex align-items-center order-info position-relative">
                <Link
                  className="border border-primary rounded p-2 mr-4 d-flex align-items-center"
                  css={{ "& i[class*='fa-exclamation-circle']": { color: 'red' } }}
                  to={`${routes.index}${routes.order.edit}${row.id}`}
                  label={row.id}
                  icon={row.priorityLevel === 1 ? 'fas fa-exclamation-circle' : undefined}
                />
                {row.outsourceVisible === true && renderOutsourceVisibleBadge()}
                <Link
                  className="open-new-tab"
                  css={{ display: 'none', position: 'absolute', right: '0' }}
                  to={`${routes.index}${routes.order.edit}${row.id}`}
                  target="_blank"
                  icon="fas fa-external-link-alt"
                  onClick={(e) => e.stopPropagation()}
                />
              </div>
            </div>
          )
        }
        return (
          <div
            css={{
              '& .order-info:hover': {
                '& .open-new-tab': {
                  display: 'block',
                },
              },
            }}>
            <div className="d-flex align-items-center order-info position-relative">
              <Link
                className="border border-primary rounded p-2 mr-1 d-flex align-items-center"
                css={{ "& i[class*='fa-exclamation-circle']": { color: 'red' } }}
                to={`${routes.index}${routes.order.edit}${row.id}`}
                icon={row.priorityLevel === 1 ? 'fas fa-exclamation-circle' : undefined}
                label={`#${row.id}`}
              />
              <div className="d-flex flex-column ml-1">
                <div className="text-bold" style={{ whiteSpace: 'nowrap' }}>
                  {getPatient(row, 'name', bucket)}
                  {row.isNightOwl && <img src={NightOwlIcon} alt="NO" />}
                  {row.isOneShip && <span className="text-gray">OneShip</span>}
                  {row.wesperFulfillment === 'DROPSHIP' && renderWesperDropshipBadge()}
                  {row.isIrhythm && <span className="text-gray">iRhythm</span>}
                  {row.isBrightree && <span>BT</span>}
                  {row.outsourceVisible === true && renderOutsourceVisibleBadge()}
                </div>
                <div css={{ fontSize: '0.9rem' }}>DOB {getPatient(row, 'dob', bucket)}</div>
              </div>
              {bucket !== 'COMPLETED' && (
                <div
                  className={`text-bold d-flex justify-content-center align-items-center mt-1 ml-4 btn btn-sm w-30 ${dayClass(
                    row,
                  )}`}
                  style={{
                    maxHeight: '22px',
                    minWidth: '48px',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                  }}>
                  <small>
                    {moment().diff(moment(row.createdAt), 'day')} {generalHelper.t('Days')}
                  </small>
                </div>
              )}
              {row.cancelled && (
                <div
                  className={`text-bold d-flex justify-content-center align-items-center mt-1 ml-2 btn btn-sm w-30 btn-danger`}
                  style={{
                    maxHeight: '22px',
                    minWidth: '48px',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                  }}>
                  <small>Cancelled</small>
                </div>
              )}
              {row.rxRecognitionProcessing && (
                <div
                  className={`text-bold d-flex justify-content-center align-items-center mt-1 ml-2 btn btn-sm w-30`}
                  style={{
                    maxHeight: '22px',
                    minWidth: '48px',
                    pointerEvents: 'none',
                    whiteSpace: 'nowrap',
                    background: '#a52ae5',
                    color: 'white',
                  }}>
                  <small>AI Verification</small>
                </div>
              )}
              <Link
                className="open-new-tab"
                css={{ display: 'none', position: 'absolute', right: '0' }}
                to={`${routes.index}${routes.order.edit}${row.id}`}
                target="_blank"
                icon="fas fa-external-link-alt"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>
        )
      },
    },
    {
      Header: 'Procedure',
      accessor: 'procedure',
      className: 'col-2',
      Cell: (props) => {
        const procedure = props.row.original?.tests[0]?.procedure

        return procedure || <></>
      },
    },
    {
      Header: 'Step',
      accessor: 'inLabStep',
      className: 'col-2',
      Cell: (props) => {
        const procedure = props.row.original?.tests[0]?.procedure

        return procedure || <></>
      },
    },
    {
      Header: 'Fax Uploaded Date',
      accessor: 'faxUploadedDate',
      className: 'col-2',
      Cell: (props) => {
        const failedFaxes = props.row.original.faxing?.filter(
          (f) => f.status === 'FAILED_PERMANENTLY',
        )
        if (failedFaxes?.length) {
          return failedFaxes.map((f) => (
            <div key={f.id}>
              <small>
                <Moment format="MM/DD/YYYY" date={f.uploadedAt} />
              </small>
            </div>
          ))
        }
        return <></>
      },
    },
    {
      Header: 'Physician Name',
      accessor: 'physician',
      className: 'col-2',
      Cell: (props) =>
        props.row.original.physician ? (
          <div className="d-flex">
            {formalName(props.row.original.physician?.firstName, props.row.original.physician?.lastName)}
          </div>
        ) : null,
    },
    {
      Header: 'Physican Fax #',
      accessor: 'faxNumber',
      className: 'col-2',
      Cell: (props) => <div className="d-flex">{props.row.original.physician?.fax}</div>,
    },
    {
      Header: 'Fax Attempts',
      accessor: 'faxAttempts',
      className: 'col-2',
      Cell: (props) => {
        const failedFaxAttempts = props.row.original.faxing
          ?.filter((f) => f.status === 'FAILED_PERMANENTLY' || f.status === 'SENT')
          ?.reduce((acc, curr) => {
            acc = acc + curr.faxAttempts
            return acc
          }, 0)

        return <small>{failedFaxAttempts}</small>
      },
    },
    {
      Header: 'Last Fax Attempt Date',
      accessor: 'lastFaxAttemptDate',
      className: 'col-2',
      Cell: (props) => {
        const failedFaxes = props.row.original.faxing
          ?.filter((f) => f.status === 'FAILED_PERMANENTLY')
          .sort((a, b) => b.when - a.when)

        if (failedFaxes?.length) {
          return (
            <div>
              <small>
                <Moment format="MM/DD/YYYY" date={failedFaxes[0].when} />
              </small>
            </div>
          )
        }
        return <></>
      },
    },
    {
      Header: 'Failure Reason',
      accessor: 'failureReason',
      className: 'col-2',
        Cell: (props) => {
          const failedFaxes = props.row.original.faxing?.filter(
            (f) => f.status === 'FAILED_PERMANENTLY',
          )
          if (!failedFaxes?.length) return <>-</>
          return (
            <>
              {failedFaxes.map((f) => {
                return (
                  <div key={f.reportId}>
                    <small>{`#${f.reportId} - ${f.failureReason}`}</small>
                  </div>
                )
              })}
            </>
          )
        },
      },
    {
      Header: 'Successful Attempts',
      accessor: 'successfulFaxes',
      className: 'col-3',
      Cell: ({ row }) => {
        const { faxing } = row.original
        const lastFax = faxing?.filter((f) => f.faxNumber).pop()

        const faxStats = (stats || {})[lastFax?.faxNumber]
        const successCount = faxStats?.SENT
        return successCount ? <small>{successCount}</small> : <>-</>
      },
    },
    {
      Header: 'In Transit',
      accessor: 'shippedAt',
      className: 'col-1',
      sortAccessor: 'shippedAt',
      Cell: ({ row }) => {
        if (!row.original.shippedAt) {
          return <span>-</span>
        }
        const shippedAt = moment(row.original.shippedAt)
        const diff = moment().diff(shippedAt, 'day')
        let className = 'btn-secondary'
        if (diff > 21) {
          className = 'btn-danger'
        } else if (diff > 15) {
          className = 'btn-orange'
        } else if (diff > 7) {
          className = 'btn-warning'
        }
        return <span className={`${className} px-2 py-1 rounded`}>{diff} Days</span>
      },
    },
    {
      Header: 'In Transit Back',
      accessor: 'shippedBackAt',
      className: 'col-1',
      sortAccessor: 'shippedBackAt',
      Cell: ({ row }) => {
        if (!row.original.shippedBackAt) {
          return <span>-</span>
        }
        const shippedBackAt = moment(row.original.shippedBackAt)
        const diff = moment().diff(shippedBackAt, 'day')
        let className = 'btn-secondary'
        if (diff > 21) {
          className = 'btn-danger'
        } else if (diff > 15) {
          className = 'btn-orange'
        } else if (diff > 7) {
          className = 'btn-warning'
        }
        return <span className={`${className} px-2 py-1 rounded`}>{diff} Days</span>
      },
    },
    {
      Header: 'First Name',
      accessor: 'firstName',
      className: 'col-1',
      sortAccessor: 'firstName',
      filterAccessor: 'firstName',
      Cell: ({ row }) => <span>{nc(row.original.patient.firstName)}</span>,
    },
    {
      Header: 'Last Name',
      accessor: 'lastName',
      className: 'col-1',
      sortAccessor: 'lastName',
      filterAccessor: 'lastName',
      Cell: ({ row }) => <span>{nc(row.original.patient.lastName)}</span>,
    },
    {
      Header: 'DOB',
      accessor: 'dob',
      className: 'col-1',
      sortAccessor: 'dob',
      filterAccessor: 'dob',
      Cell: ({ row }) => <span>{moment(row.original.patient.dob).format('MM/DD/YYYY')}</span>,
    },
    {
      Header: 'Order Age',
      accessor: 'orderAge',
      className: 'col-1',
      sortAccessor: 'orderAge',
      filterAccessor: 'orderAge',
      Cell: ({ row }) => (
        <small style={{ whiteSpace: 'nowrap', fontSize: '0.9rem' }}>
          {moment().diff(moment(row.original.createdAt), 'day')} {generalHelper.t('Days')}
        </small>
      ),
    },
    {
      Header: 'OneShip',
      accessor: 'isOneShip',
      className: 'col-1',
      sortAccessor: 'isOneShip',
      filterAccessor: 'isOneShip',
      Cell: ({ row }) => (
        <small style={{ whiteSpace: 'nowrap' }}>{row.original.isOneShip ? 'Y' : '-'}</small>
      ),
    },
    {
      id: 'status',
      Header: 'Status',
      className: 'col-1',
      accessor: 'bucket',
      canFilter: false,
      style: { verticalAlign: 'middle' },
      Cell: ({ row }) => (
        <span>{_.startCase(row.original.bucket.replace(/_/g, ' ').toLowerCase())}</span>
      ),
    },
    {
      id: 'hstBucket',
      Header: 'Sleep Testing Bucket',
      className: 'col-2',
      accessor: 'hstBucket',
      canFilter: false,
      style: { verticalAlign: 'middle' },
      Cell: ({ row }) => (
        <span>
          {_.startCase(
            row.original.labBuckets
              ?.filter(
                (i) =>
                  i.startsWith('HST_') || i.startsWith('PSG_') || i === 'OPEN' || i === 'COMPLETED',
              )
              .join(', ')
              .replace(/HST_/g, '')
              .replace(/PSG_/g, '')
              .replace(/_/g, ' ')
              .toLowerCase(),
          )}
        </span>
      ),
    },
    {
      id: 'officeName',
      Header: 'DME Client',
      accessor: 'officeName',
      className: 'col-1',
      canFilter: false,
      Cell: ({ row }) => <span css={{ fontSize: '0.9rem' }}>{row.original?.officeName}</span>,
    },
    {
      Header: 'Progress',
      accessor: 'progress',
      className: 'col-4',
      disableSortBy: true,
      Cell: (props) => {
        const order = props.row.original
        return <OrderProgressBar context="LAB" orderData={order} testType={testType} />
      },
    },
    {
      Header: 'Device Checked Out',
      className: 'col-1',
      accessor: 'deviceAssignedAt',
      sortInverted: true,
      canFilter: false,
      Cell: ({ row }) => {
        const { deviceAssignedAt } = row.original
        const days = deviceAssignedAt ? moment().diff(moment(deviceAssignedAt), 'day') : null
        if (days === null) {
          return <span>-</span>
        }
        return (
          <div
            className={`text-bold d-flex justify-content-center align-items-center btn btn-sm w-30 ${dayClass(
              row,
            )}`}
            style={{
              maxHeight: '22px',
              minWidth: '48px',
              maxWidth: '75px',
              pointerEvents: 'none',
              whiteSpace: 'nowrap',
            }}>
            {days === 0 ? (
              <span>Today</span>
            ) : (
              <span>
                {days} {days > 1 ? 'days' : 'day'}
              </span>
            )}
          </div>
        )
      },
    },
    {
      Header: 'Notes',
      className: 'col-1',
      accessor: 'notes',
      disableSortBy: true,
      Cell: ({ row }) => {
        const notes = row?.original?.notes
        const notesCount = Number(notes?.count ?? 0) + Number(notes?.schedulingCount ?? 0)
        return notesCount > 0 ? (
          <LazyNotesPopover orderId={Number(row.original.id)}>
            <div className="w-100">
              <Pill
                css={css`
                  background-color: lightyellow;
                  border: 1px solid #ffc10780;
                `}
                to={`${routes.index}${routes.order.edit}${row.original.id}`}>
                {notesCount} Note{notesCount > 1 ? 's' : ''}
              </Pill>
            </div>
          </LazyNotesPopover>
        ) : (
          '-'
        )
      },
    },
    {
      Header: 'Conditions',
      className: 'col-2',
      accessor: 'tests',
      Cell: ({ row }) => (
        <ul className="tcWidget_list p-0">
          {Array.isArray(row.original.tests) ? (
            row.original.tests.map((t, i) => (
              <li key={i}>
                <span>{`Night ${i + 1}`}: </span>
                <span className={`setting ${!t.oxygen ? 'setting-air' : ''}`}>
                  {t.oxygen ? 'OXYGEN' : 'ROOM AIR'}{' '}
                  {t.oxygen ? <span className="value">{t.oxygenLpm}</span> : ''}
                </span>
                {t.papType ? (
                  <span
                    className={`type ${
                      t.papType === 'BiPAP' ? 'type-bipap' : t.papType === 'CPAP' ? 'type-cpap' : ''
                    }`}>
                    {' '}
                    - {t.papType}{' '}
                    <span className="value">
                      {t.papType === 'APAP'
                        ? `${t.apapMin} - ${t.apapMax}`
                        : t.papType === ' - BiPAP '
                          ? `${t.bipapI} - ${t.bipapE}`
                          : ` - ${t.cpap} `}
                    </span>
                  </span>
                ) : null}
              </li>
            ))
          ) : (
            <p className="noData">No Nights Currently Added.</p>
          )}
        </ul>
      ),
    },
    {
      Header: 'Physician',
      className: 'col-4',
      accessor: 'physician',
      canFilter: false,
      Cell: ({ row }) => {
        return row.original.physician ? (
          <div style={{ minWidth: '12vw' }}>
            {formalName(row.original?.physician?.firstName, row.original?.physician?.lastName)}
          </div>
        ) : (
          <>-</>
        )
      },
    },
    {
      Header: 'Device Type',
      accessor: 'neededDeviceType',
      className: 'col-1',
      sortAccessor: ['neededDeviceType'],
      Cell: ({ row }) => <span>{row.original.neededDeviceType || '-'}</span>,
    },
    {
      Header: 'Current Issues',
      className: 'col-2',
      accessor: 'tasks',
      disableSortBy: true,
      Cell: ({ row }) => {
        const { tasks } = row.original
        if (tasks?.length === 0) return '-'

        return (
          <PopoverWrapper
            placement="left"
            customHeader="Current Issues"
            customContent={
              <div className="p-2">
                {tasks
                  ?.filter((t) => t.blocking && !t.resolved)
                  ?.map((t) => <p>{_.startCase(t.namespace.replace(/_/g, ' ').toLowerCase())}</p>)}
              </div>
            }>
            <span>
              <Link
                to={`${routes.index}${routes.order.edit}`}
                label={<>{tasks?.length ?? 0} issue(s)</>}
              />
            </span>
          </PopoverWrapper>
        )
      },
    },
    {
      Header: 'Direct Messages',
      className: 'col-2',
      accessor: 'messages',
      disableSortBy: true,
      Cell: ({ row }) => {
        const { messages } = row.original
        const unread = messages?.count - messages?.read
        if (!messages?.count) return '-'
        return (
          <LazyMessagesPopover orderId={Number(row.original.id)}>
            <span>
              <Link
                to={`${routes.index}${routes.order.edit}${row.id}`}
                label={
                  <>
                    {unread ?? 0} unread
                    <br />
                    {messages?.count} total
                  </>
                }
              />
            </span>
          </LazyMessagesPopover>
        )
      },
    },
    {
      Header: 'Patient SMSs',
      className: 'col-2',
      accessor: 'smsMessages',
      disableSortBy: true,
      Cell: ({ row }) => {
        const { patientSms } = row.original
        const unread = patientSms?.count - patientSms?.read
        if (!patientSms?.count) return '-'

        return (
          <LazySmsPopover orderId={Number(row.original.id)}>
            <span>
              <Link
                to={`${routes.index}${routes.order.edit}${Number(row.original.id)}`}
                label={
                  <>
                    {unread} unread
                    <br />
                    {patientSms?.count} total
                  </>
                }
              />
            </span>
          </LazySmsPopover>
        )
      },
    },
    {
      Header: 'Primary Payor Status',
      className: 'col-1',
      accessor: 'primaryPayerStatus',
      Cell: ({ row }) => {
        const primaryPayer = row.original.payers?.find((i) => i.position === 1)

        return primaryPayer ? (
          <span>
            {PAYER_STATUS_OPTIONS.find((i) => i.key === primaryPayer.status)?.value ??
              'Not Started'}
          </span>
        ) : (
          <></>
        )
      },
    },
    {
      Header: 'Primary Payor',
      className: 'col-2',
      accessor: 'primaryPayer',
      Cell: ({ row }) => renderPayer(row.original, 1, { fontSize: '0.9rem' }),
    },
    {
      Header: 'Primary Member ID',
      className: 'col-1',
      accessor: 'primaryPayerId',
      sortAccessor: 'primaryPayerId',
      Cell: ({ row }) => (
        <span>{row.original.payers?.find((p) => p.position === 1)?.memberId}</span>
      ),
    },
    {
      Header: 'Secondary Payor Status',
      className: 'col-1',
      accessor: 'secondaryPayerStatus',
      disableSortBy: true,
      Cell: ({ row }) => {
        const secondaryPayer = row.original.payers?.find((i) => i.position === 2)

        return secondaryPayer ? (
          <span style={{ whiteSpace: 'nowrap' }}>
            {PAYER_STATUS_OPTIONS.find((i) => i.key === secondaryPayer.status)?.value ??
              'Not Started'}
          </span>
        ) : (
          <></>
        )
      },
    },
    {
      Header: 'Secondary Payor',
      className: 'col-2',
      accessor: 'secondaryPayer',
      Cell: ({ row }) => renderPayer(row.original, 2, { whiteSpace: 'nowrap' }),
    },
    {
      Header: 'Secondary Member ID',
      className: 'col-1',
      accessor: 'secondaryPayerId',
      sortAccessor: 'secondaryPayerId',
      Cell: ({ row }) => (
        <span>{row.original.payers?.find((p) => p.position === 2)?.memberId}</span>
      ),
    },
    {
      Header: 'Follow-up Date',
      className: 'col-1',
      accessor: 'followUpDate:TPD',
      Cell: ({ row }) => {
        const followUp = row.original.followUpsList.find((f) => f.namespace === 'TPD')
        return renderFollowUpDate(followUp)
      },
    },
    {
      Header: 'Follow-up Date',
      className: 'col-1',
      accessor: 'payerFollowUpDate',
      Cell: ({ row }) => (
        <span>
          {row.original.payerFollowUpDate
            ? moment(row.original.payerFollowUpDate).format('MM/DD/YYYY')
            : ''}
        </span>
      ),
    },
    {
      Header: 'Follow-up Date',
      className: 'col-1',
      accessor: 'followUpDate:HST_PATIENT_CONSENT',
      Cell: ({ row }) => {
        const followUp = row.original.followUpsList?.find(
          (i) => i.namespace === 'HST_PATIENT_CONSENT',
        )
        return renderFollowUpDate(followUp)
      },
    },
    {
      Header: 'Follow-up Date',
      className: 'col-1',
      accessor: 'followUpDate:HST_PAPERWORK',
      Cell: ({ row }) => {
        const followUp = row.original.followUpsList?.find((i) => i.namespace === 'HST_PAPERWORK')
        return renderFollowUpDate(followUp)
      },
    },
    {
      Header: 'Follow-up Date',
      className: 'col-1',
      accessor: 'followUpDate:HST',
      Cell: ({ row }) => {
        const followUps = [...(row.original.followUpsList ?? [])].sort(
          (a, b) => new Date(b.addedAt).getTime() - new Date(a.addedAt).getTime(), // a.date > b.date ? -1 : 1,
        )

        // Namespace resolution is shared with the human-follow-up filter so the chip a
        // user sees and the rows the filter returns can never disagree (TES-3011).
        const targetNamespace = getFollowUpNamespace(testType, bucket)
        const followUp =
          followUps?.find((f) => f.namespace === targetNamespace) ?? followUps?.[0]
        const date = followUp?.date

        return (
          <PopoverWrapper
            placement="left"
            customHeader="Follow-ups"
            customContent={
              <div className="p-2">
                <p
                  className="p-2 mb-3"
                  css={css`
                    background-color: lightyellow;
                    border: 1px solid #ffc10780;
                  `}>
                  {followUps.map((f) => (
                    <div>
                      <small>
                        {f.namespace}: {moment(f.date).format('MM/DD/YYYY')}
                        {f.humanFollowUpRequired ? ' - Human follow-up required' : ''}
                      </small>
                    </div>
                  ))}
                </p>
              </div>
            }>
            {date ? renderFollowUpDate(followUp) : <></>}
          </PopoverWrapper>
        )
      },
    },
    {
      Header: 'Auth Required',
      className: 'col-1',
      accessor: 'authorizationRequired',
      Cell: ({ row }) => <span>{row.original.authorizationRequired ? 'Y' : '-'}</span>,
    },
    {
      Header: 'Tertiary Payor',
      className: 'col-1',
      accessor: 'tertiaryPayer',
      Cell: ({ row }) => <span>{row.original.hasPayer3 ? 'Y' : '-'}</span>,
    },
    {
      Header: 'Test Type',
      className: 'col-1',
      accessor: 'testType',
      Cell: ({ row }) => {
        const testType = row.original.test_type || row.original.testType
        if (testType === 'PSG') {
          return <span>InLab - {row.original?.tests?.[0]?.procedure}</span>
        }
        return <span>{testType}</span>
      },
    },
    {
      Header: 'Order Completion',
      className: 'col-1',
      accessor: 'orderCompletion',
      Cell: ({ row }) => (
        <span>
          {row.original.reportGeneratedAt
            ? moment(row.original.reportGeneratedAt).format('MM/DD/YYYY')
            : '-'}
        </span>
      ),
    },
    {
      Header: 'Pending Report Date',
      className: 'col-1',
      accessor: 'reportPendingAt',
      Cell: ({ row }) => (
          <span>
          {row.original.reportPendingAt
              ? moment(row.original.reportPendingAt).format('MM/DD/YYYY')
              : '-'}
        </span>
      ),
    },
    {
      Header: 'Completed Date',
      className: 'col-1',
      accessor: 'completedAt',
      Cell: ({ row }) => (
        <span>
          {row.original.completedAt ? moment(row.original.completedAt).format('MM/DD/YYYY') : '-'}
        </span>
      ),
    },
    {
      Header: 'Days of Service',
      className: 'col-1',
      accessor: 'daysOfService',
      Cell: ({ row }) => <span>{row.original.tests?.length}</span>,
    },
    {
      Header: 'Faxed Status',
      className: 'col-1',
      accessor: 'faxedStatus',
      Cell: ({ row }) => {
        const { faxing } = row.original
        const allSent = !!faxing?.length && faxing?.every((i) => i.status === 'SENT')
        const allFailed = !!faxing?.length && faxing?.every((i) => i.status === 'FAILED')

        return (
          <span>
            {allSent && 'Sent'}
            {allFailed && 'Failed'}
            {!allSent && !allFailed && 'Pending'}
          </span>
        )
      },
    },
    {
      Header: 'Payor Notes',
      className: 'col-1',
      accessor: 'payerNotes',
      Cell: ({ row }) => (
        <PopoverWrapper
          placement="left"
          customHeader="Payor Notes"
          customContent={
            <div className="p-2">
              <p
                className="p-2 mb-3"
                css={css`
                  background-color: lightyellow;
                  border: 1px solid #ffc10780;
                `}>
                {row.original.payersNote}
              </p>
            </div>
          }>
          {row.original.payersNote ? (
            <div className="w-100">
              <Pill
                to={`${routes.index}${routes.order.edit}${row.original.id}`}
                css={css`
                  background-color: lightyellow;
                  border: 1px solid #ffc10780;
                `}>
                {row.original.payersNote?.length > 20
                  ? row.original.payersNote.slice(0, 20).concat('...')
                  : row.original.payersNote}
              </Pill>
            </div>
          ) : (
            <></>
          )}
        </PopoverWrapper>
      ),
    },
    {
      Header: 'Auth State',
      className: 'col-1',
      accessor: 'authorizationState',
      Cell: ({ row }) => (
        <span css={{ whiteSpace: 'nowrap' }}>
          {row.original.authorizationState === 'PENDING' && 'Not Started'}
          {row.original.authorizationState === 'STARTED' && 'Started'}
        </span>
      ),
    },
    {
      Header: 'Auth Exp',
      className: 'col-1',
      accessor: 'authorizationValidTo',
      Cell: ({ row }) => (
        <span>
          {row.original.authorizationValidTo &&
            moment(row.original.authorizationValidTo).format('MM/DD/YYYY')}
        </span>
      ),
    },
    {
      Header: 'Scheduled Date',
      className: 'col-1',
      accessor: 'inLabTestingScheduledAt',
      Cell: ({ row }) => (
        <span>
          {row.original.inLabTestingScheduledAt
            ? moment.utc(row.original.inLabTestingScheduledAt).format('MM/DD/YYYY')
            : '-'}
        </span>
      ),
    },
    {
      Header: 'Action',
      className: 'col-1',
      accessor: 'updateAuthorization',
      Cell: ({ row }) => (
        <span onClick={(e) => e.stopPropagation()}>
          <Button
            size="sm"
            variant="outline-primary"
            onClick={(e) => {
              setShowAuthorizationDialog(row.original.id)
            }}>
            Update
          </Button>
        </span>
      ),
    },
    {
      accessor: 'updateDate',
      Header: 'Last Updated',
      Cell: ({ row }) =>
        row.original?.labBucketChangedAt
          ? moment(row.original.labBucketChangedAt).format('MM/DD/YYYY')
          : '-',
    },
    {
      Header: 'Referring Provider',
      className: 'col-2',
      accessor: 'referringProvider',
      canFilter: false,
      Cell: ({ row }) => {
        return row.original.physician ? (
          <>
            {formalName(row.original?.physician?.firstName, row.original?.physician?.lastName)}
          </>
        ) : (
          <>-</>
        )
      },
    },
    {
      Header: 'Follow-Up Care Provider',
      className: 'col-2',
      accessor: 'followUpCarePhysician',
      canFilter: false,
      Cell: ({ row }) => {
        const followUpPhysician = row.original?.followUpCarePhysician
        const label = isOrganization(followUpPhysician?.providerType)
          ? followUpPhysician?.organizationName
          : formalName(followUpPhysician?.firstName, followUpPhysician?.lastName)

        return followUpPhysician ? label : '-'
      },
    },
    {
      Header: 'Follow Up Date',
      className: 'col-1',
      accessor: 'followUpCareInitiatedAt',
      Cell: ({ row }) => {
        return row.original.followUpCareInitiatedAt
          ? moment(row.original.followUpCareInitiatedAt).format('MM/DD/YYYY')
          : '-'
      },
    },
    {
      Header: 'Referring Provider',
      className: 'col-2',
      accessor: 'referringProvider',
      canFilter: false,
      Cell: ({ row }) => {
        return row.original.physician ? (
          <>
            {formalName(row.original?.physician?.firstName, row.original?.physician?.lastName)}
          </>
        ) : (
          <>-</>
        )
      },
    },
    {
      Header: 'Follow Up Date',
      className: 'col-1',
      accessor: 'followUpCareInitiatedAt',
      Cell: ({ row }) => {
        return row.original.followUpCareInitiatedAt
          ? moment(row.original.followUpCareInitiatedAt).format('MM/DD/YYYY')
          : '-'
      },
    },
    {
      accessor: 'cancellationReason',
      Header: 'Cancellation Reason',
      className: 'col-2',
      disableSortBy: true,
      Cell: ({ row }) => {
        if (!row.original.cancelled) return <span className="text-gray">-</span>
        return (
          <div className="d-flex flex-column">
            <span className="text-bold">{row.original.cancellationReason || 'No reason provided'}</span>
            {row.original.cancellationDescription && (
              <small className="text-gray">{row.original.cancellationDescription}</small>
            )}
          </div>
        )
      },
    },
  ]

  const currentViewColumns =
    bucketColumnDefaults[testType === 'HST' ? 'HST' : testType === 'PSG' ? 'PSG' : 'POXCOX'][bucket]
      ?.map((col) => allColumns.find((c) => c.accessor === col))
      ?.filter((i) => i) || []

  return currentViewColumns
}

export default _columnsFunction
